import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { SearchIcon, CloseIcon } from '@/components/icons';
import { Button, Pagination } from '@/components/ui';
import {
  useSearchPressReleases,
  useHomePageHighlights,
  useUpdateHomePageHighlights,
  usePressReleases,
} from '@/lib/api/hooks/press-release';
import { PressRelease } from '@/lib/api/types';
import { WebsitePageSection } from '@/types';
import {
  Star,
  Lightbulb,
  Eye,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Maximize2,
  Trash2,
  // SlidersHorizontal,
} from 'lucide-react';

// ============================================================================
// Constants
// ============================================================================

const MAX_HIGHLIGHTS = 3;
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_COVER_IMAGE =
  'https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png';

const BRAND_COLOR_DARK = '#681628';

/** index 0 => "Main Feature", indices 1+ => "Secondary" */
const slotLabel = (index: number) => (index === 0 ? 'Main Feature' : 'Secondary');

// ============================================================================
// Small local hooks / helpers
// ============================================================================

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

const formatPublishedDate = (dateString: string | null): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Describes what the "Assign to slot" dropdown should offer for a given
 * current highlight count. Slots must be filled in order (1, then 2, then 3),
 * but any already-filled slot can be replaced directly.
 */
interface SlotOption {
  slotNumber: number;
  label: string;
  disabled: boolean;
}

const getSlotOptions = (highlights: PressRelease[]): SlotOption[] =>
  Array.from({ length: MAX_HIGHLIGHTS }, (_, i) => {
    const slotNumber = i + 1;
    if (i < highlights.length) {
      return { slotNumber, label: `Slot ${slotNumber} · Replace current`, disabled: false };
    }
    if (i === highlights.length) {
      return { slotNumber, label: `Slot ${slotNumber} · Empty`, disabled: false };
    }
    return { slotNumber, label: `Slot ${slotNumber} · Locked`, disabled: true };
  });

// ============================================================================
// Thumbnail (shared, with graceful image fallback)
// ============================================================================

const PressReleaseThumbnail: React.FC<{ src?: string | null; alt: string; className: string }> = ({
  src,
  alt,
  className,
}) => {
  const [imgSrc, setImgSrc] = useState(src || DEFAULT_COVER_IMAGE);
  useEffect(() => setImgSrc(src || DEFAULT_COVER_IMAGE), [src]);

  return (
    <div className={`bg-gray-100 rounded overflow-hidden flex-shrink-0 ${className}`}>
      <img
        src={imgSrc}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover"
        onError={() => setImgSrc(DEFAULT_COVER_IMAGE)}
      />
    </div>
  );
};

const PressReleaseMeta: React.FC<{ pr: PressRelease }> = ({ pr }) => {
  if (!pr.datePublished && !pr.createdByUsername) return null;
  return (
    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
      {pr.datePublished && (
        <p className="text-xs text-gray-500">
          <span className="font-medium">Published date:</span> {formatPublishedDate(pr.datePublished)}
        </p>
      )}
      {pr.createdByUsername && (
        <p className="text-xs text-gray-500">
          <span className="font-medium">Published by:</span> {pr.createdByUsername}
        </p>
      )}
    </div>
  );
};

// ============================================================================
// Live Layout Preview
// ============================================================================

const PreviewPlaceholderLines: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <div className="flex-1 space-y-2 py-1">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="h-2.5 bg-gray-200 rounded"
        style={{ width: `${100 - i * 15}%` }}
      />
    ))}
  </div>
);

interface LiveLayoutSlotProps {
  slotNumber: number;
  pr: PressRelease | null;
  isMain: boolean;
}

const LiveLayoutSlot: React.FC<LiveLayoutSlotProps> = React.memo(({ slotNumber, pr, isMain }) => (
  <div
    className={`rounded-lg border p-3 flex gap-3 ${isMain ? 'flex-[2]' : 'flex-1'} ${
      isMain
        ? 'border-2 shadow-sm'
        : pr
          ? 'border-gray-200'
          : 'border-dashed border-gray-300'
    }`}
    style={isMain ? { borderColor: BRAND_COLOR_DARK } : undefined}
  >
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-2">
        <span
          className={`w-5 h-5 rounded flex items-center justify-center text-xs font-semibold text-white flex-shrink-0`}
          style={{ backgroundColor: isMain ? BRAND_COLOR_DARK : '#6B7280' }}
        >
          {slotNumber}
        </span>
        <span className="text-xs font-medium text-gray-600 truncate">
          Slot {slotNumber} · {slotLabel(slotNumber - 1)}
        </span>
      </div>
      <div className="flex gap-2 flex-1">
        {pr ? (
          <PressReleaseThumbnail src={pr.coverImage} alt={pr.title} className="w-20 h-14" />
        ) : (
          <div className="w-20 h-14 rounded bg-gray-100 flex-shrink-0" />
        )}
        <PreviewPlaceholderLines count={isMain ? 4 : 3} />
      </div>
    </div>
  </div>
));
LiveLayoutSlot.displayName = 'LiveLayoutSlot';

const LiveLayoutPreview: React.FC<{ highlights: PressRelease[] }> = ({ highlights }) => (
  <div className="p-4 border-b border-gray-200">
    <div className="flex items-center gap-2 mb-3 text-gray-700">
      <Eye className="w-4 h-4" aria-hidden="true" />
      <span className="text-sm font-medium">Live Layout Preview</span>
    </div>
    <div className="flex gap-4">
      {Array.from({ length: MAX_HIGHLIGHTS }).map((_, i) => (
        <LiveLayoutSlot key={i} slotNumber={i + 1} pr={highlights[i] ?? null} isMain={i === 0} />
      ))}
    </div>
  </div>
);

// ============================================================================
// Currently Selected — slot row
// ============================================================================

interface SelectedSlotRowProps {
  pr: PressRelease;
  index: number; // position within highlights array
  totalSlots: number;
  isDragging: boolean;
  onRemove: (index: number) => void;
  onMakeMain: (index: number) => void;
  onMoveSecondary: (index: number, direction: -1 | 1) => void;
  onDragStart: (index: number) => void;
  onDrop: () => void;
}

const SelectedSlotRow: React.FC<SelectedSlotRowProps> = React.memo(
  ({ pr, index, totalSlots, isDragging, onRemove, onMakeMain, onMoveSecondary, onDragStart, onDrop }) => {
    const isMain = index === 0;
    const slotNumber = index + 1;

    const canMoveUp = !isMain && index > 1;
    const canMoveDown = !isMain && index < totalSlots - 1;

    return (
      <div
        className={`flex items-center gap-3 p-3 rounded-lg border bg-white transition-opacity ${
          isMain ? 'border-l-4' : 'border-gray-200'
        } ${isDragging ? 'opacity-40' : 'opacity-100'}`}
        style={isMain ? { borderColor: '#e5e7eb', borderLeftColor: BRAND_COLOR_DARK } : undefined}
        draggable={!isMain}
        onDragStart={() => !isMain && onDragStart(index)}
        onDragOver={(e) => {
          if (isMain) return;
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (!isMain) onDrop();
        }}
      >
        {/* Drag handle — only interactive for secondary slots */}
        <div
          className={`flex-shrink-0 ${isMain ? 'text-gray-300 cursor-default' : 'text-gray-400 cursor-grab active:cursor-grabbing'}`}
          aria-hidden="true"
        >
          <GripVertical className="w-4 h-4" />
        </div>

        {/* Slot badge */}
        <span
          className="w-6 h-6 rounded flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
          style={{ backgroundColor: isMain ? BRAND_COLOR_DARK : '#4B5563' }}
        >
          {slotNumber}
        </span>

        {isMain && <Star className="w-4 h-4 text-amber-500 flex-shrink-0" aria-hidden="true" />}

        <div className="flex-1 min-w-0 flex items-start gap-3">
          <PressReleaseThumbnail src={pr.coverImage} alt={pr.title} className="w-16 h-12 hidden sm:block" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-500">
              Slot {slotNumber} · {slotLabel(index)}
            </p>
            <h5 className="text-sm font-semibold text-gray-900 truncate">{pr.title}</h5>
            <PressReleaseMeta pr={pr} />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {isMain ? (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 whitespace-nowrap">
              Appears first &amp; largest
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={() => onMoveSecondary(index, -1)}
                disabled={!canMoveUp}
                aria-label="Move up"
                className="p-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => onMoveSecondary(index, 1)}
                disabled={!canMoveDown}
                aria-label="Move down"
                className="p-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
              >
                <ArrowDown className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => onMakeMain(index)}
                className="flex items-center gap-1 px-2 py-1.5 rounded border border-emerald-200 text-emerald-600 text-xs font-medium hover:bg-emerald-50 whitespace-nowrap"
              >
                <Maximize2 className="w-3.5 h-3.5" />
                Make Main
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => onRemove(index)}
            aria-label={`Remove ${pr.title} from Slot ${slotNumber}`}
            className="p-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }
);
SelectedSlotRow.displayName = 'SelectedSlotRow';

// ============================================================================
// Available content — browsable row with per-row slot assignment
// ============================================================================

interface AvailableContentRowProps {
  pr: PressRelease;
  slotOptions: SlotOption[];
  onAssign: (pr: PressRelease, slotNumber: number) => void;
}

const AvailableContentRow: React.FC<AvailableContentRowProps> = React.memo(
  ({ pr, slotOptions, onAssign }) => {
    const [selectedSlot, setSelectedSlot] = useState<string>('');

    const handleApply = useCallback(() => {
      if (!selectedSlot) return;
      onAssign(pr, Number(selectedSlot));
      setSelectedSlot('');
    }, [selectedSlot, pr, onAssign]);

    return (
      <div className="flex items-center gap-4 p-3 rounded-lg border border-gray-200">
        <PressReleaseThumbnail src={pr.coverImage} alt={pr.title} className="w-24 h-16" />
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 text-sm truncate">{pr.title}</h4>
          <PressReleaseMeta pr={pr} />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            value={selectedSlot}
            onChange={(e) => setSelectedSlot(e.target.value)}
            aria-label={`Assign "${pr.title}" to slot`}
            className="h-9 text-sm border border-gray-200 rounded px-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#951F39]"
          >
            <option value="">Assign to slot</option>
            {slotOptions.map((opt) => (
              <option key={opt.slotNumber} value={opt.slotNumber} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleApply}
            disabled={!selectedSlot}
            className="h-9 px-3 text-sm font-medium rounded border border-gray-200 text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed enabled:text-white enabled:border-transparent"
            style={selectedSlot ? { backgroundColor: BRAND_COLOR_DARK } : undefined}
          >
            Apply
          </button>
        </div>
      </div>
    );
  }
);
AvailableContentRow.displayName = 'AvailableContentRow';

// ============================================================================
// Main component
// ============================================================================

interface InlinePressReleaseEditorProps {
  data: WebsitePageSection;
  onCancel: () => void;
}

const InlinePressReleaseEditor: React.FC<InlinePressReleaseEditorProps> = ({ onCancel }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  // Ordered, compact list: index 0 = Main Feature, subsequent indices = Secondary,
  // in display order. There are never gaps, so slot number is always index + 1.
  const [highlights, setHighlights] = useState<PressRelease[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const selectedIds = useMemo(() => highlights.map((pr) => pr.pressReleaseId), [highlights]);

  const [page, setPage] = useState(1);

  const isSearching = !!debouncedSearchQuery;

  const { data: allPressReleasesData, isFetching: isAllFetching, isError: isAllError } = usePressReleases(
    { page, size: PAGE_SIZE },
    { status: 'PUBLISH' }
  );

  const {
    data: searchResultsData,
    isFetching: isSearchFetching,
    isError: isSearchError,
  } = useSearchPressReleases({ page, size: PAGE_SIZE }, debouncedSearchQuery, isSearching);

  const totalPages = useMemo(() => {
    if (isSearching) {
      return searchResultsData?.data?.totalPage ?? 1;
    } else {
      return allPressReleasesData?.data?.totalPage ?? 1;
    }
  }, [isSearching, searchResultsData, allPressReleasesData]);

  const isFetchingPressReleases = isSearching ? isSearchFetching : isAllFetching;



  const { data: highlightsData } = useHomePageHighlights();
  const updateHighlightsMutation = useUpdateHomePageHighlights();

  const isError = isSearching ? isSearchError : isAllError;

  const allPressReleases: PressRelease[] = useMemo(
    () => (isSearching ? searchResultsData?.data?.data : allPressReleasesData?.data?.data) || [],
    [isSearching, searchResultsData, allPressReleasesData]
  );

  const browsablePressReleases = useMemo(
    () => allPressReleases.filter((pr) => !selectedIds.includes(pr.pressReleaseId)),
    [allPressReleases, selectedIds]
  );

  const slotOptions = useMemo(() => getSlotOptions(highlights), [highlights]);

  useEffect(() => {
    if (highlightsData?.data) {
      // Server order is assumed to already encode slot order (index 0 = main).
      setHighlights(highlightsData.data.slice(0, MAX_HIGHLIGHTS));
    }
  }, [highlightsData]);

  // ---- Slot mutations -------------------------------------------------------

  const handleAssign = useCallback((pr: PressRelease, slotNumber: number) => {
    setHighlights((prev) => {
      const targetIndex = slotNumber - 1;
      const next = [...prev];
      if (targetIndex < next.length) {
        next[targetIndex] = pr; // replace an occupied slot
      } else if (targetIndex === next.length) {
        next.push(pr); // fill the next empty slot
      }
      return next;
    });
  }, []);

  const handleRemove = useCallback((index: number) => {
    setHighlights((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleMakeMain = useCallback((index: number) => {
    setHighlights((prev) => {
      if (index === 0) return prev;
      const item = prev[index];
      const rest = prev.filter((_, i) => i !== index);
      return [item, ...rest];
    });
  }, []);

  const handleMoveSecondary = useCallback((index: number, direction: -1 | 1) => {
    setHighlights((prev) => {
      const targetIndex = index + direction;
      if (index < 1 || targetIndex < 1 || targetIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  }, []);

  // Drag-and-drop reordering — restricted to secondary slots (index >= 1),
  // matching the "drag to reorder secondary items" affordance.
  const handleDragStart = useCallback((index: number) => setDragIndex(index), []);


  const handleDropOnIndex = useCallback(
    (toIndex: number) => {
      setHighlights((prev) => {
        if (dragIndex === null || dragIndex < 1 || toIndex < 1 || dragIndex === toIndex) return prev;
        const next = [...prev];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
      });
      setDragIndex(null);
    },
    [dragIndex]
  );

  const handleSave = useCallback(async () => {
    try {
      const landingPagePositions: Record<string, number> = {}    
      highlights.forEach((pr, index) => {
        landingPagePositions[pr.pressReleaseId as keyof typeof landingPagePositions] = index + 1; // slot number = index + 1
      });
      await updateHighlightsMutation.mutateAsync({ pressReleaseIds: selectedIds, pressReleaseHighlightType: 'HOME_PAGE_HERO', landingPagePositions });
      onCancel();
    } catch {
      // Error toast handled inside the mutation's onError.
    }
  }, [highlights, updateHighlightsMutation, selectedIds, onCancel]);

  const handleClearSearch = useCallback(() => setSearchQuery(''), []);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 p-5 border-b border-gray-200">
        <div>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-xl text-gray-900">Manage Hero Highlights</h3>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            <span className="font-semibold">Slot 1</span> is the <span className="font-semibold">Main Feature</span>{' '}
            and appears largest and first on the homepage.
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-semibold">Slots 2 and 3</span> are{' '}
            <span className="font-semibold">Secondary Highlights</span> and appear on the right side.
          </p>
        </div>

        <div className="flex items-start gap-4 flex-shrink-0">
          <div className="hidden lg:flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-sm">
            <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Tip:</span> The article in Slot 1 will appear largest and first on the
              homepage hero section.
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 p-1" aria-label="Close">
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Live Layout Preview */}
      <LiveLayoutPreview highlights={highlights} />

      {/* Currently Selected */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">
            Currently Selected ({highlights.length} of {MAX_HIGHLIGHTS} slots filled)
          </h4>
          {highlights.length > 1 && (
            <span className="text-xs text-gray-400">Drag to reorder secondary items or use arrows.</span>
          )}
        </div>

        {highlights.length === 0 ? (
          <div className="text-center py-8 text-gray-400 border border-dashed border-gray-300 rounded-lg text-sm">
            No highlights selected yet. Assign content from the list below.
          </div>
        ) : (
          <div className="space-y-2">
            {highlights.map((pr, index) => (
              <SelectedSlotRow
                key={pr.pressReleaseId}
                pr={pr}
                index={index}
                totalSlots={highlights.length}
                isDragging={dragIndex === index}
                onRemove={handleRemove}
                onMakeMain={handleMakeMain}
                onMoveSecondary={handleMoveSecondary}
                onDragStart={handleDragStart}
                onDrop={() => handleDropOnIndex(index)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Search + filters */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <label htmlFor="press-release-search" className="sr-only">
              Search published content
            </label>
            <SearchIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="press-release-search"
              type="text"
              placeholder="Search published content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-9 pr-9 bg-white text-sm text-gray-900 placeholder-gray-400 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#951F39] focus:border-transparent"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Placeholder for future content-type filtering once the API supports it */}
          {/* <select
            aria-label="Filter by content type"
            className="h-10 text-sm border border-gray-200 rounded px-3 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#951F39]"
            defaultValue="ALL"
          >
            <option value="ALL">All content types</option>
          </select>

          <button
            type="button"
            aria-label="More filters"
            className="h-10 w-10 flex items-center justify-center border border-gray-200 rounded text-gray-500 hover:bg-gray-50"
          >
            <SlidersHorizontal className="w-4 h-4" />
          </button> */}
        </div>
      </div>

      {/* Available content */}
      <div className="max-h-[400px] overflow-y-auto p-4 space-y-3">
        <h4 className="text-sm font-medium text-gray-700 mb-1">Available Published Content</h4>
        {isFetchingPressReleases ? (
          <div className="text-center py-8 text-gray-500">Loading press releases...</div>
        ) : isError ? (
          <div className="text-center py-8 text-gray-500">
            Something went wrong loading press releases. Please try again.
          </div>
        ) : browsablePressReleases.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No press releases found</div>
        ) : (
          browsablePressReleases.map((pr) => (
            <AvailableContentRow key={pr.pressReleaseId} pr={pr} slotOptions={slotOptions} onAssign={handleAssign} />
          ))
        )}
        
        {/* Pagination */}
       <Pagination
                                  currentPage={page}
                                  totalPages={totalPages}
                                  onPageChange={setPage}
                                  className="p-4"
                                />
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={updateHighlightsMutation.isPending}>
          {updateHighlightsMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default React.memo(InlinePressReleaseEditor);